import { Input, Textarea, Typography } from "@stardust-ds/react"
import { useCallback, useContext, useEffect, useState } from "react"
import { isMobile } from "react-device-detect"
import intl from "react-intl-universal"

import CameraPictureIcon from "../../../assets/icons/CameraPictureIcon"
import CameraVideoIcon from "../../../assets/icons/CameraVideoIcon"
import AuthContext from "../../../contexts/Auth"
import Button from "../../atoms/Button"
import FileInput from "../../atoms/FileInput"
import Radios from "../../molecules/Radios"
import styles from "./index.module.scss"
import * as yup from "yup"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { fnFormatDate } from "../../../utils/date"
import RcmpModalInfo from "../ModalInfo"
import RcmpModalMediaCapture from "../ModalMediaCapture"
import RcmpActivityEvidencesList from "./ActivityEvidencesList"
import RcmpModalVideoCapture from "../ModalVideoCapture"
import useDataApi from "../../../hooks/useDataApi"
import {
  fnDeleteEvidence,
  fnGetDocumentsGED,
} from "../../../services/evidences"
import RcmpAlert from "../../atoms/Alert"
import Select from "../../atoms/Select"

let renderValueEvidence

const C_VIDEO_MODE_USER = "user"
const C_VIDEO_MODE_ENVIRONMENT = "environment"

function RcmpMediaEvidence(props) {
  const { fnSubmitEvidence, cCamState } = props

  const [bOpenModalMediaCapture, fnSetBOpenModalMediaCapture] = useState(false)
  const [bOpenModalVideoCapture, fnSetBOpenModalVideoCapture] = useState(false)
  const [aDevices, fnSetADevices] = useState([])
  const [cVideoMode, fnSetCVideoMode] = useState(C_VIDEO_MODE_USER)

  const schemaFile = yup.array().of(
    yup.object().shape({
      opae_cvalue: yup.mixed(),
      opae_ctype: yup.string().default("MEDIA"),
      opae_cfilename: yup.string().required(),
    })
  )

  const { register, handleSubmit, getValues, setValue, watch, reset } = useForm(
    {
      resolver: yupResolver(schemaFile),
    }
  )

  const fnHandleGetDevices = useCallback(
    (mediaDevices) =>
      fnSetADevices(mediaDevices?.filter(({ kind }) => kind === "videoinput")),
    [fnSetADevices]
  )

  useEffect(() => {
    if (typeof navigator?.mediaDevices?.enumerateDevices !== "function") return

    navigator.mediaDevices?.enumerateDevices()?.then(fnHandleGetDevices)
  }, [fnHandleGetDevices])

  function fnSubmitMediaEvidence() {
    const fileData = {
      opae_cvalue: getValues("opae_cvalue"),
      opae_ctype: "MEDIA",
      opae_cfilename: getValues("opae_cfilename"),
    }
    fnSubmitEvidence({ items: [fileData] })
    fnSetBOpenModalMediaCapture(false)
    fnSetBOpenModalVideoCapture(false)
    reset({
      opae_cfilename: "",
    })
  }

  function fnCloseModalMediaCapture() {
    fnSetBOpenModalMediaCapture(false)
  }

  function fnCloseModalVideoCapture() {
    fnSetBOpenModalVideoCapture(false)
  }

  function fnSwitchCamera() {
    fnSetCVideoMode((prev) =>
      prev === C_VIDEO_MODE_USER ? C_VIDEO_MODE_ENVIRONMENT : C_VIDEO_MODE_USER
    )
  }

  const bHasMediaName = Boolean(watch("opae_cfilename")?.length)
  const bHasCamera = cCamState === "granted"
  const bCanSwitchCamera = bHasCamera && isMobile

  return (
    <div className={styles.evidenceMediaContent}>
      <Input
        label={intl.get("ActivityEvidence.media_name")}
        isFullWidth
        {...register("opae_cfilename")}
      />
      <div className={styles.evidenceMediaButtons}>
        <Button
          iconLeft={<CameraPictureIcon className={styles.svgButton} />}
          variant={"outline"}
          onClick={fnSetBOpenModalMediaCapture}
          disabled={!bHasCamera || !bHasMediaName}
        >
          {intl.get("ActivityEvidence.photo")}
        </Button>
        <Button
          variant={"outline"}
          iconLeft={<CameraVideoIcon className={styles.svgButton} />}
          disabled={!bHasCamera || !bHasMediaName}
          onClick={fnSetBOpenModalVideoCapture}
        >
          {intl.get("ActivityEvidence.video")}
        </Button>
      </div>
      <RcmpModalMediaCapture
        cTitle={intl.get("ActivityEvidence.take_photo")}
        bIsOpen={bOpenModalMediaCapture}
        buttonSaveText={intl.get("save")}
        fnHandleCancel={fnCloseModalMediaCapture}
        setValue={setValue}
        handleSubmit={handleSubmit}
        fnSubmitImagaEvidence={fnSubmitMediaEvidence}
        cFileName={getValues("opae_cfilename")}
        fnSwitchCamera={fnSwitchCamera}
        cVideoMode={cVideoMode}
        bCanSwitchCamera={bCanSwitchCamera}
      />
      <RcmpModalVideoCapture
        bIsOpen={bOpenModalVideoCapture}
        fnOnClose={fnCloseModalVideoCapture}
        handleSubmit={handleSubmit}
        setValue={setValue}
        fnSubmitVideoEvidence={fnSubmitMediaEvidence}
        cFileName={watch("opae_cfilename")}
        fnSwitchCamera={fnSwitchCamera}
        cVideoMode={cVideoMode}
        bCanSwitchCamera={bCanSwitchCamera}
      />
    </div>
  )
}

function RcmpFileEvidence(props) {
  const { fnSubmitEvidence } = props

  function fnSubmitFileEvidence(data) {
    fnSubmitEvidence({ items: data })
  }
  return (
    <div className={styles.evidenceMediaContent}>
      <FileInput fnFileSubmit={fnSubmitFileEvidence}>
        <div className={styles.evidencesSentsContainer}>
          <Typography color="#415364" type="l3" textAlign="left">
            {intl.get("ActivityEvidence.evidence_sent")}
          </Typography>
        </div>
      </FileInput>
    </div>
  )
}

function RcmpDocumentEvidence(props) {
  const { fnSubmitEvidence } = props

  const [aDocuments, fnSetADocuments] = useState([])
  const [oSelectedDocument, fnSetOSelectedDocument] = useState(null)
  const [bLoading, fnSetBLoading] = useState(false)

  const [oGetDocumentsGEDRes, fnGetDocumentsGEDReq] =
    useDataApi(fnGetDocumentsGED)

  useEffect(() => {
    if (oGetDocumentsGEDRes.loading) return
    (async () => {
      const response = await fnGetDocumentsGEDReq()
      fnSetADocuments(fnDocumentsToOptions(response?.data))
    })()
  }, [])

  function fnDocumentsToOptions(aRawDocuments) {
    const aConvertOptions = aRawDocuments?.map((d) => ({
      label: d?.doc_ctitle,
      value: d?.doc_nid,
    }))

    return aConvertOptions
  }

  async function fnHandleOnSelect(oDocument) {
    if (document?.body?.lastChild) {
      document.body.lastChild.style.pointerEvents = "none";
      document.body.lastChild.click()
    }
    if (!oDocument?.value) return

    fnSetOSelectedDocument(oDocument)
    const oDocumentSelected = oDocument
    fnSetOSelectedDocument(null)
    fnSetBLoading(true)
    const body = {
      items: [{ opae_doc_nid: oDocumentSelected?.value, opae_ctype: "GED" }],
    }

    await fnSubmitEvidence(body)
    fnSetBLoading(false)
    if (document?.body?.lastChild) {
      document.body.lastChild.style.pointerEvents = "auto";
    }

  }

  return (
    <div className={styles.evidenceMediaContent}>
      <Select
        label={intl.get("ActivityEvidence.ged_document_evidence_type")}
        placeholder={intl.get("type_or_select")}
        searchable
        clearable
        value={oSelectedDocument}
        options={aDocuments}
        onSelect={fnHandleOnSelect}
        disabled={bLoading}
      />
    </div>
  )
}

function RcmpTextEvidence(props) {
  const {
    bOpenModalAlert,
    fnSetBConfirmModalAlert,
    fnConfirmChangePage,
    fnSubmitEvidence,
    fnSetCTextEvidenceValidator,
    fnSetIsDirty,
  } = props
  const { oUser } = useContext(AuthContext)
  const cUserName = oUser?.data?.user_cname
  const nUserId = oUser?.data?.user_nid
  const [aTextEvidences, fnSetATextEvidences] = useState([])

  const schemaText = yup.object().shape({
    items: yup.array().of(
      yup.object().shape({
        opae_cvalue: yup
          .string()
          .required(intl.get("ActivityEvidence.required_field")),
        opae_ctype: yup.string().default("TEXT"),
      })
    ),
  })

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
    watch,
  } = useForm({
    resolver: yupResolver(schemaText),
  })

  function fnAddNewText(data) {
    if (!getValues("opae_cvalue")) return
    const cNewTextEvidence = {
      items: [{ opae_cvalue: data?.opae_cvalue, opae_ctype: "TEXT" }],
    }
    fnSubmitEvidence(cNewTextEvidence)
    reset({
      opae_cvalue: "",
    })
    fnSetCTextEvidenceValidator("")
    return fnSetATextEvidences([
      ...aTextEvidences,
      {
        opae_cvalue: data.opae_cvalue,
        cUserName: cUserName,
        dDate: fnFormatDate(new Date()),
        nUserId: nUserId,
      },
    ])
  }

  useEffect(() => {
    fnSetIsDirty(isDirty)
  }, [watch()])

  return (
    <div className={styles.evidenceMediaContent}>
      <form id="addTextEvidence" onSubmit={handleSubmit(fnAddNewText)}>
        <Textarea
          placeholder={intl.get("ActivityEvidence.type_it")}
          label={intl.get("ActivityEvidence.text_label")}
          required
          {...register("opae_cvalue", {
            onChange: (e) => fnSetCTextEvidenceValidator(e.target.value),
          })}
        />
        <div className={styles.buttonTextEvidence}>
          <Button type="submit" variant="outline">
            {intl.get("sent")}
          </Button>
        </div>
      </form>
      <RcmpModalInfo
        fnHandleCancel={fnSetBConfirmModalAlert}
        bIsOpen={bOpenModalAlert}
        title={intl.get("warning")}
        buttonCancelText={intl.get("cancel")}
        buttonSaveText={intl.get("continue")}
        description={intl.get("ActivityEvidence.warning_text_evidence")}
        fnHandleConfirm={fnConfirmChangePage}
        size
      />
    </div>
  )
}

export default function RcmpActivityEvidence(props) {
  const {
    fnSetBOpenModalMediaCapture,
    fnSetBOpenModalVideoCapture,
    fnSubmitEvidence,
    aEvidences,
    fnSetAEvidences,
    bReadOnly,
    fnEditEvidenceSubmit,
    fnSetIsDirty,
  } = props

  const aInitialEvidenceType = [
    {
      label: intl.get("ActivityEvidence.media_evidence_type"),
      name: "activityEvidenceType",
      value: "media",
      checked: true,
    },
    {
      label: intl.get("ActivityEvidence.file_evidence_type"),
      name: "activityEvidenceType",
      value: "file",
    },
    {
      label: intl.get("ActivityEvidence.ged_document_evidence_type"),
      name: "activityEvidenceType",
      value: "document",
    },
    {
      label: intl.get("ActivityEvidence.text_evidence_type"),
      name: "activityEvidenceType",
      value: "text",
    },
  ]

  const [aEvidenceType, fnSetAevidenceType] = useState(aInitialEvidenceType)
  const [cHandleRender, fnSetCHandleRender] = useState("media")
  const [bOpenModalAlert, fnSetBModalAlert] = useState(false)
  const [bIsUploaded, fnSetBIsUploaded] = useState(false)
  const [oDeleteEvidenceRes, fnDeleteEvidenceReq] =
    useDataApi(fnDeleteEvidence)
  const [cTextEvidenceValidator, fnSetCTextEvidenceValidator] = useState("")
  const [cCamState, fnSetCCamState] = useState(null)

  const schema = yup.object().shape({
    opae_cvalue: yup.string(),
  })

  const { register, reset, watch } = useForm({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    navigator.permissions
      .query({ name: "camera" })
      .then((oPermissionStatus) => {
        fnSetCCamState(oPermissionStatus?.state)
      })
      .catch((e) => {
        fnSetCCamState(e)
      })
  }, [])

  function fnSetEvidenteType(value) {
    const aEvidenceTypeAux = aEvidenceType.map((e) => ({
      ...e,
      checked: e?.value === value ? true : false,
    }))

    fnSetAevidenceType(aEvidenceTypeAux)
  }

  function fnConfirmChangePage() {
    reset({
      opae_cvalue: "",
    })
    fnSetBModalAlert(false)
    fnSetEvidenteType(renderValueEvidence)
    return fnSetCHandleRender(renderValueEvidence)
  }

  function fnCCancelChangePage() {
    const radio = document.getElementById("text")
    radio.checked = true
    fnSetBModalAlert(false)
  }
  function fnChangeEvidenceType(value) {
    renderValueEvidence = value
    if (
      cHandleRender === "text" &&
      value !== "text" &&
      cTextEvidenceValidator !== ""
    ) {
      return fnSetBModalAlert("true")
    }

    fnSetEvidenteType(value)
    return fnSetCHandleRender(value)
  }

  function fnHandleOnChange(value) {
    renderValueEvidence = value

    if (cHandleRender === "text") {
      fnChangeEvidenceType(value)
      return
    }

    fnSetEvidenteType(value)
    fnSetCHandleRender(value)
  }

  useEffect(() => {
    fnSetBIsUploaded(false)
  }, [aEvidences])

  async function fnHandleSubmitEvidence(oEvidence) {
    fnSetBIsUploaded(true)
    await fnSubmitEvidence?.(oEvidence)
  }

  const removeFile = (i) => {
    const fileRemove = [...aEvidences].filter(
      (evidence) => evidence.opae_nid !== i
    )
    fnSetAEvidences(fileRemove)
  }

  async function fnDeleteEvidenceSubmit(nEvidenceId) {
    const response = await fnDeleteEvidenceReq(nEvidenceId)
    response.success && removeFile(nEvidenceId)
  }

  if (bReadOnly) {
    return (
      <RcmpActivityEvidencesList
        aEvidences={aEvidences}
        fnSetNEvidenceIdDelete={fnDeleteEvidenceSubmit}
        bIsUploaded={bIsUploaded}
        bReadOnly={bReadOnly}
      />
    )
  }

  return (
    <>
      <RcmpAlert
        cType="error"
        bIsOpen={cCamState !== null && cCamState !== "granted"}
      >
        {intl.get("ActivityEvidence.load_cam_error")}
      </RcmpAlert>
      <Radios
        radios={aEvidenceType}
        value={cHandleRender}
        onChange={fnHandleOnChange}
      />
      {cHandleRender === "media" && (
        <RcmpMediaEvidence
          fnSetBOpenModalMediaCapture={fnSetBOpenModalMediaCapture}
          fnSetBOpenModalVideoCapture={fnSetBOpenModalVideoCapture}
          register={register}
          watch={watch}
          fnSubmitEvidence={fnHandleSubmitEvidence}
          cCamState={cCamState}
        />
      )}
      {cHandleRender === "file" && (
        <RcmpFileEvidence fnSubmitEvidence={fnHandleSubmitEvidence} />
      )}
      {cHandleRender === "document" && (
        <RcmpDocumentEvidence fnSubmitEvidence={fnHandleSubmitEvidence} />
      )}
      {cHandleRender === "text" && (
        <RcmpTextEvidence
          bOpenModalAlert={bOpenModalAlert}
          fnConfirmChangePage={fnConfirmChangePage}
          fnSetBConfirmModalAlert={fnCCancelChangePage}
          fnSetBModalAlert={fnSetBModalAlert}
          fnSubmitEvidence={fnHandleSubmitEvidence}
          fnSetCTextEvidenceValidator={fnSetCTextEvidenceValidator}
          fnSetIsDirty={fnSetIsDirty}
        />
      )}
      <RcmpActivityEvidencesList
        aEvidences={aEvidences}
        fnSetNEvidenceIdDelete={fnDeleteEvidenceSubmit}
        bIsUploaded={bIsUploaded}
        fnEditEvidenceSubmit={fnEditEvidenceSubmit}
      />
    </>
  )
}
